import { withCsrf } from "@/lib/api/csrf";

const csrfFetch = withCsrf(fetch);

export interface PushConfigResponse {
  readonly publicKey: string;
  readonly supported: boolean;
  readonly subscribed: boolean;
}

export interface PushSubscriptionStatus {
  readonly subscribed: boolean;
}

export type PushFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface PushRequestOptions {
  readonly signal?: AbortSignal;
}

export function browserPushSupported(
  navigatorRef: Navigator | undefined = typeof navigator === "undefined"
    ? undefined
    : navigator,
  windowRef: Window | undefined = typeof window === "undefined"
    ? undefined
    : window,
): boolean {
  // Defensive against SSR / unit-test environments AND Chrome 90 WebView on
  // older Android where one of these globals can be undefined or read-only.
  // Each `in` check is wrapped: a missing global is a hard `false` (no push),
  // never a thrown ReferenceError.
  if (!navigatorRef || !windowRef) return false;
  if (!("serviceWorker" in navigatorRef)) return false;
  if (!("PushManager" in windowRef)) return false;
  if (!("Notification" in windowRef)) return false;
  return true;
}

export function urlBase64ToUint8Array(value: string): Uint8Array {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) {
    bytes[index] = raw.charCodeAt(index);
  }
  return bytes;
}

export function urlBase64ToApplicationServerKey(value: string): ArrayBuffer {
  const bytes = urlBase64ToUint8Array(value);
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

export async function fetchPushStatus(
  fetcher: PushFetch = fetch,
  options: PushRequestOptions = {},
): Promise<PushConfigResponse> {
  const response = await fetcher(
    "/api/v2/push/subscription",
    withPushSignal({ credentials: "include" }, options.signal),
  );
  if (!response.ok)
    return { publicKey: "", supported: false, subscribed: false };
  return response.json() as Promise<PushConfigResponse>;
}

export async function enablePushNotifications(
  fetcher: PushFetch | undefined = csrfFetch,
  options: PushRequestOptions = {},
): Promise<void> {
  const pushFetch = fetcher ?? csrfFetch;
  const status = await fetchPushStatus(pushFetch, options);
  if (!status.supported || !status.publicKey)
    throw new Error("web push public key is missing");
  const registration = await navigator.serviceWorker.ready;
  const permission = await Notification.requestPermission();
  if (permission !== "granted")
    throw new Error(`notification permission=${permission}; expected granted`);
  const subscription = await createPushSubscription(
    registration,
    status.publicKey,
  );
  await savePushSubscription(subscription, pushFetch, options);
}

export async function createPushSubscription(
  registration: ServiceWorkerRegistration,
  publicKey: string,
): Promise<PushSubscription> {
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToApplicationServerKey(publicKey),
  });
}

export async function savePushSubscription(
  subscription: PushSubscription,
  fetcher: PushFetch = csrfFetch,
  options: PushRequestOptions = {},
): Promise<PushSubscriptionStatus> {
  const response = await fetcher(
    "/api/v2/push/subscriptions",
    withPushSignal(
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(subscription.toJSON()),
      },
      options.signal,
    ),
  );
  if (!response.ok)
    throw new Error(`push subscription failed status=${response.status}`);
  return response.json() as Promise<PushSubscriptionStatus>;
}

function withPushSignal(
  init: RequestInit,
  signal: AbortSignal | undefined,
): RequestInit {
  return signal ? { ...init, signal } : init;
}
